/**
 * <hauser-user-chip> — Custom Element ESM (ha/358 Phase F).
 *
 * Affiche l'avatar + nom de l'utilisateur courant en topbar. Le click ouvre
 * un menu déroulant (Compte / Profil / Sécurité / Déconnexion).
 *
 * Modes SSR / hydration :
 *   - data-user='{...}' (JSON) : SSR placeholder, pas de fetch
 *   - data-user absent         : fetch /ha/form `hauser.user.current` au mount
 *
 * Cas anonyme :
 *   - data-user='null' (string)             → bouton "Se connecter"
 *   - fetch renvoie null                     → bouton "Se connecter"
 *
 * Sans Shadow DOM, cohérent avec <ha-form>/<ha-toast>. CSS scopé via classes
 * préfixées `.hauser-chip-*` (cf. global stylesheet, hap/css ou app overlay).
 */

import { fetchCurrentUser, normalizeHauserUser, type HauserUser } from "../api.js";

export class HauserUserChip extends HTMLElement {
    private _user: HauserUser | null = null;
    private _menuOpen = false;
    private _docClickHandler: ((e: Event) => void) | null = null;

    static get observedAttributes(): string[] {
        return ["data-user"];
    }

    connectedCallback(): void {
        const ssr = this._readSsrUser();
        if (ssr !== undefined) {
            this._user = ssr;
            this.render();
            return;
        }
        // SSR absent → fetch async, render placeholder en attendant.
        this.render();
        void this._hydrate();
    }

    disconnectedCallback(): void {
        this._unbindDocClick();
    }

    attributeChangedCallback(name: string): void {
        if (!this.isConnected) return;
        if (name === "data-user") {
            const ssr = this._readSsrUser();
            if (ssr !== undefined) {
                this._user = ssr;
                this.render();
            }
        }
    }

    /** Test seam : force le user et re-render. */
    setUser(user: HauserUser | null): void {
        this._user = user;
        this._menuOpen = false;
        this.render();
    }

    private _readSsrUser(): HauserUser | null | undefined {
        const raw = this.getAttribute("data-user");
        if (raw === null) return undefined;
        if (raw === "null" || raw === "") return null;
        try {
            const parsed: unknown = JSON.parse(raw);
            if (parsed === null) return null;
            return normalizeHauserUser(parsed);
        } catch {
            return undefined;
        }
    }

    private async _hydrate(): Promise<void> {
        const user = await fetchCurrentUser();
        if (!this.isConnected) return;
        this._user = user;
        this.render();
    }

    private render(): void {
        if (this._user === null) {
            this._renderAnonymous();
            return;
        }
        this._renderConnected(this._user);
    }

    private _renderAnonymous(): void {
        this._unbindDocClick();
        this._menuOpen = false;
        const pathname = typeof location !== "undefined" ? location.pathname : "/";
        const configuredHref = this.getAttribute("data-anonymous-href");
        const href = configuredHref !== null && configuredHref !== ""
            ? configuredHref
            : `/login?r=${encodeURIComponent(pathname)}`;
        const label = this.getAttribute("data-anonymous-label") ?? "Se connecter";
        const extraClass = this.getAttribute("data-anonymous-class");
        const a = document.createElement("a");
        a.className = extraClass !== null && extraClass !== ""
            ? `hauser-chip hauser-chip--anonymous ${extraClass}`
            : "hauser-chip hauser-chip--anonymous";
        a.setAttribute("href", href);
        a.setAttribute("data-state", "anonymous");
        a.textContent = label;
        this.replaceChildren(a);
    }

    private _renderConnected(user: HauserUser): void {
        const wrapper = document.createElement("div");
        wrapper.className = "hauser-chip hauser-chip--connected";
        wrapper.setAttribute("data-state", "connected");

        const button = document.createElement("button");
        button.type = "button";
        button.className = "hauser-chip__button";
        button.setAttribute("aria-haspopup", "menu");
        button.setAttribute("aria-expanded", String(this._menuOpen));

        const avatar = this._renderAvatar(user);
        const label = document.createElement("span");
        label.className = "hauser-chip__label";
        label.textContent = this._displayName(user);
        button.replaceChildren(avatar, label);
        button.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            this._menuOpen = !this._menuOpen;
            button.setAttribute("aria-expanded", String(this._menuOpen));
            menu.hidden = !this._menuOpen;
            if (this._menuOpen) {
                this._bindDocClick();
            } else {
                this._unbindDocClick();
            }
        });

        const menu = this._renderMenu();
        menu.hidden = !this._menuOpen;

        wrapper.replaceChildren(button, menu);
        this.replaceChildren(wrapper);
    }

    private _renderAvatar(user: HauserUser): HTMLElement {
        const span = document.createElement("span");
        span.className = "hauser-chip__avatar";
        if (user.picture !== null && user.picture !== "") {
            const img = document.createElement("img");
            img.src = user.picture;
            img.alt = "";
            img.loading = "lazy";
            span.appendChild(img);
        } else {
            span.textContent = this._initials(user);
            span.setAttribute("aria-hidden", "true");
        }
        return span;
    }

    private _renderMenu(): HTMLElement {
        const menu = document.createElement("ul");
        menu.className = "hauser-chip__menu";
        menu.setAttribute("role", "menu");
        for (const item of this._menuItems()) {
            const li = document.createElement("li");
            li.setAttribute("role", "none");
            const a = document.createElement("a");
            a.setAttribute("role", "menuitem");
            a.className = "hauser-chip__menu-item";
            a.href = item.href;
            a.textContent = item.label;
            li.appendChild(a);
            menu.appendChild(li);
        }
        return menu;
    }

    /**
     * Items du menu. Configurables via l'attribut `data-menu` (JSON `[{label,href}]`) :
     * le token `{dashboard}` en href est remplacé par `user.dashboardUrl` (résolu par
     * rôle côté serveur, staff→pro→garant) et l'item est omis si aucun dashboard n'est
     * disponible. Sans `data-menu` → menu par défaut de l'espace connecté HorApp.
     */
    private _menuItems(): Array<{ label: string; href: string }> {
        const dashboard = this._user?.dashboardUrl ?? "";
        const raw = this.getAttribute("data-menu");
        let configured: Array<{ label: string; href: string }> | null = null;
        if (raw !== null && raw !== "") {
            try {
                const parsed: unknown = JSON.parse(raw);
                if (Array.isArray(parsed)) {
                    configured = parsed
                        .filter((e): e is Record<string, unknown> => typeof e === "object" && e !== null)
                        .map(e => ({
                            label: typeof e.label === "string" ? e.label : "",
                            href:  typeof e.href === "string" ? e.href : "",
                        }))
                        .filter(e => e.label !== "" && e.href !== "");
                }
            } catch {
                configured = null;
            }
        }
        const items = configured ?? [
            { label: "Compte",      href: "/compte" },
            { label: "Profil",      href: "/profil" },
            { label: "Sécurité",    href: "/securite" },
            { label: "Déconnexion", href: "/logout" },
        ];
        return items
            .map(it => it.href === "{dashboard}" ? { label: it.label, href: dashboard } : it)
            .filter(it => it.href !== "");
    }

    private _displayName(user: HauserUser): string {
        if (user.firstname !== "" && user.lastname !== "") {
            return `${user.firstname} ${user.lastname}`;
        }
        if (user.nickname !== "") return user.nickname;
        if (user.name !== "") return user.name;
        if (user.email !== "") return user.email;
        return "Mon compte";
    }

    private _initials(user: HauserUser): string {
        const fn = user.firstname.charAt(0).toUpperCase();
        const ln = user.lastname.charAt(0).toUpperCase();
        if (fn !== "" && ln !== "") return fn + ln;
        if (user.nickname !== "") return user.nickname.charAt(0).toUpperCase();
        if (user.name !== "") return user.name.charAt(0).toUpperCase();
        if (user.email !== "") return user.email.charAt(0).toUpperCase();
        return "?";
    }

    private _bindDocClick(): void {
        if (this._docClickHandler !== null) return;
        this._docClickHandler = (e: Event) => {
            if (!this.contains(e.target as Node)) {
                this._menuOpen = false;
                this.render();
            }
        };
        document.addEventListener("click", this._docClickHandler);
    }

    private _unbindDocClick(): void {
        if (this._docClickHandler === null) return;
        document.removeEventListener("click", this._docClickHandler);
        this._docClickHandler = null;
    }
}

if (typeof customElements !== "undefined" && !customElements.get("hauser-user-chip")) {
    customElements.define("hauser-user-chip", HauserUserChip);
}
